/**
 * Whether a JWT's `exp` has passed, decided locally — no Keycloak adapter involved.
 *
 * Needed because bootstrap has a path where the adapter can't answer: `keycloak.init`
 * is bounded by a timeout (a `checkLoginIframe` handshake can hang forever behind
 * blocked third-party cookies), and when it doesn't settle the app falls back to the
 * access token in localStorage. That token can be hours old, so "there is a token"
 * is not the same as "the user is signed in" — trusting it sent expired credentials
 * on every request and surfaced as unexplained failures instead of a login.
 */

/** Treat a token about to expire as expired — covers clock drift and request latency. */
const CLOCK_SKEW_MS = 30_000

/** base64url → the UTF-8 JSON it encodes. */
const decodeJwtSegment = (segment: string): string => {
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/')
  const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')
  const binary = atob(padded)
  // A claim may hold non-ASCII (names, e-mail display names); `atob` yields latin1
  // bytes, so re-decode them as UTF-8 rather than feeding mojibake to JSON.parse.
  try {
    return decodeURIComponent(
      Array.from(binary, (char) => {
        const code = char.charCodeAt(0).toString(16).padStart(2, '0')
        return `%${code}`
      }).join(''),
    )
  } catch {
    // Not valid UTF-8 — no issuer should produce that (RFC 7519), so fall back to the
    // raw bytes rather than declaring the token unreadable. Being wrong at this point
    // signs a working user out.
    return binary
  }
}

/**
 * True when `token` is missing, unreadable, or past its `exp` (minus a safety skew).
 *
 * Unreadable counts as expired: a Keycloak access token is always a JWT, so anything
 * else is corrupted storage, and the cost of being wrong is one silent SSO redirect.
 * A token with no `exp` claim is left alone — nothing says it has lapsed.
 */
export const isJwtExpired = (token?: string | null): boolean => {
  if (!token) return true

  const payloadSegment = token.split('.')[1]
  if (!payloadSegment) return true

  try {
    const { exp } = JSON.parse(decodeJwtSegment(payloadSegment)) as {
      exp?: number
    }
    if (typeof exp !== 'number') return false
    return exp * 1000 <= Date.now() + CLOCK_SKEW_MS
  } catch {
    return true
  }
}
